import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
    ShoppingBag, Menu, X, User, LogOut, Home as HomeIcon, Heart, Vote,
    Ticket, HandCoins, Sparkles, Shield, Users, Target, Phone, GraduationCap,
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_3d19f3b3-7a64-4230-9811-c39a1dbb89c8/artifacts/pcow4efy_ChatGPT%20Image%20May%2015%2C%202026%2C%2008_56_17%20PM.png";

// Single source of truth for the site navigation — used by the desktop
// left rail and the mobile hamburger. Each entry maps to an icon component so
// the rail can show a tight icon+label stack without breaking on long words.
const NAV_LINKS = [
    { to: "/", label: "Home", icon: HomeIcon },
    { to: "/shop", label: "Shop", icon: ShoppingBag },
    { to: "/donate", label: "Donate", icon: Heart },
    { to: "/polls", label: "Polls", icon: Vote },
    { to: "/raffles", label: "Raffles", icon: Ticket },
    { to: "/services", label: "Free board", icon: HandCoins },
    { to: "/directory", label: "Directory", icon: Phone },
    { to: "/learn", label: "Learn", icon: GraduationCap },
    { to: "/impact", label: "Impact", icon: Sparkles },
    { to: "/ledger", label: "Ledger", icon: Shield },
    { to: "/about#get-involved", label: "Get involved", icon: Users },
    { to: "/about", label: "Mission", icon: Target },
];

const Navbar = () => {
    const { itemCount } = useCart();
    const { user, logout } = useAuth();
    const [open, setOpen] = React.useState(false);
    const location = useLocation();

    React.useEffect(() => setOpen(false), [location.pathname]);

    const isActive = (to) => {
        if (to === "/") return location.pathname === "/";
        // hashed routes (#get-involved): activate when the about page is current
        const [path] = to.split("#");
        return location.pathname === path || location.pathname.startsWith(path + "/");
    };

    return (
        <>
            {/* ============================================================
                DESKTOP LEFT RAIL — fixed icon-only sidebar on lg+ screens.
                Pages get `lg:pl-20` baseline padding in App.js's <main>.
            ============================================================ */}
            <aside
                className="hidden lg:flex fixed left-0 top-0 h-screen w-20 flex-col items-stretch bg-[#FDFBF7] border-r border-black/5 z-40"
                data-testid="site-rail"
                aria-label="Main navigation"
            >
                <Link to="/" data-testid="rail-logo-link" className="flex items-center justify-center h-20 border-b border-black/5">
                    <img src={LOGO_URL} alt="Reddog Foundation" className="h-12 w-auto" />
                </Link>
                <nav className="flex-1 overflow-y-auto py-3 flex flex-col gap-0.5">
                    {NAV_LINKS.map((l) => {
                        const Icon = l.icon;
                        const active = isActive(l.to);
                        return (
                            <Link
                                key={l.to}
                                to={l.to}
                                data-testid={`rail-${l.label.toLowerCase().replace(/\s/g, "-")}-link`}
                                title={l.label}
                                className={`mx-2 flex flex-col items-center justify-center rounded-xl py-2.5 text-[10px] uppercase tracking-wider font-bold transition-colors ${
                                    active
                                        ? "bg-[#E53935] text-white"
                                        : "text-[#4A5568] hover:text-[#E53935] hover:bg-[#F2D8D5]/40"
                                }`}
                            >
                                <Icon className="w-5 h-5 mb-1" strokeWidth={1.5} />
                                <span className="leading-tight">{l.label}</span>
                            </Link>
                        );
                    })}
                </nav>
            </aside>

            {/* ============================================================
                TOP HEADER — slim bar with logo (mobile) / spacer (desktop)
                + user/cart/hamburger controls on the right.
            ============================================================ */}
            <header
                className="sticky top-0 z-30 glass-header border-b border-black/5 lg:pl-20"
                data-testid="site-header"
            >
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="flex items-center justify-between h-16 lg:h-14">
                        {/* Mobile-only logo (desktop logo lives in the rail) */}
                        <Link to="/" className="flex items-center gap-3 lg:hidden" data-testid="nav-logo-link">
                            <img src={LOGO_URL} alt="Reddog Foundation" className="h-10 w-auto" />
                        </Link>
                        <span className="hidden lg:block" />

                        <div className="flex items-center gap-3">
                            {user ? (
                                <div className="hidden md:flex items-center gap-3">
                                    {user.role === "admin" && (
                                        <>
                                            <Link to="/admin" data-testid="nav-admin-link" className="text-sm font-medium text-[#1C252E] hover:text-[#E53935] transition-colors">Admin</Link>
                                            <Link to="/account" data-testid="nav-account-link-admin" className="text-sm font-medium text-[#1C252E] hover:text-[#E53935] transition-colors" title="Account & security keys">Account</Link>
                                        </>
                                    )}
                                    {user.role !== "admin" && (
                                        <Link to="/account" data-testid="nav-account-link" className="text-sm font-medium text-[#1C252E] hover:text-[#E53935] transition-colors">My account</Link>
                                    )}
                                    <span className="text-sm text-[#4A5568] truncate max-w-[140px]" data-testid="nav-user-name">{user.name || user.email}</span>
                                    <button onClick={logout} data-testid="nav-logout-btn" className="p-2 rounded-full hover:bg-[#F4F1EA] transition-colors" aria-label="Log out">
                                        <LogOut className="w-4 h-4 text-[#1C252E]" strokeWidth={1.5} />
                                    </button>
                                </div>
                            ) : (
                                <Link to="/login" data-testid="nav-login-link" className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-[#1C252E] hover:text-[#E53935] transition-colors">
                                    <User className="w-4 h-4" strokeWidth={1.5} />
                                    Sign in
                                </Link>
                            )}

                            <Link to="/cart" data-testid="nav-cart-link" className="relative p-2 rounded-full hover:bg-[#F4F1EA] transition-colors" aria-label={itemCount > 0 ? `Cart, ${itemCount} item${itemCount === 1 ? "" : "s"}` : "Cart"}>
                                <ShoppingBag className="w-5 h-5 text-[#1C252E]" strokeWidth={1.5} />
                                {itemCount > 0 && (
                                    <span data-testid="nav-cart-count" aria-hidden="true" className="absolute -top-1 -right-1 bg-[#E53935] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{itemCount}</span>
                                )}
                            </Link>

                            <button
                                onClick={() => setOpen(!open)}
                                data-testid="nav-mobile-toggle"
                                className="lg:hidden p-2 rounded-full hover:bg-[#F4F1EA]"
                                aria-label={open ? "Close menu" : "Open menu"}
                                aria-expanded={open}
                                aria-controls="mobile-nav-panel"
                            >
                                {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile slide-down nav panel */}
                {open && (
                    <div id="mobile-nav-panel" className="lg:hidden border-t border-black/5 bg-[#FDFBF7]">
                        <nav className="px-6 py-4 flex flex-col gap-3" aria-label="Mobile">
                            {NAV_LINKS.map((l) => {
                                const Icon = l.icon;
                                const active = isActive(l.to);
                                return (
                                    <Link
                                        key={l.to}
                                        to={l.to}
                                        data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}-link`}
                                        className={`inline-flex items-center gap-3 text-base font-medium ${active ? "text-[#E53935]" : "text-[#1C252E]"} hover:text-[#E53935]`}
                                    >
                                        <Icon className="w-5 h-5" strokeWidth={1.5} /> {l.label}
                                    </Link>
                                );
                            })}
                            <div className="border-t border-black/5 mt-2 pt-3 flex flex-col gap-3">
                                {user ? (
                                    <>
                                        {user.role === "admin" && (
                                            <Link to="/admin" className="text-base font-medium text-[#1C252E]" data-testid="mobile-nav-admin-link">Admin</Link>
                                        )}
                                        <Link to="/account" className="text-base font-medium text-[#1C252E]" data-testid="mobile-nav-account-link">
                                            {user.role === "admin" ? "Account & security keys" : "My account"}
                                        </Link>
                                        <button onClick={logout} className="text-base font-medium text-[#1C252E] text-left" data-testid="mobile-nav-logout-btn">Sign out</button>
                                    </>
                                ) : (
                                    <Link to="/login" className="text-base font-medium text-[#1C252E]" data-testid="mobile-nav-login-link">Sign in</Link>
                                )}
                            </div>
                        </nav>
                    </div>
                )}
            </header>
        </>
    );
};

export default Navbar;
